import styles from './Chip.module.css'

/**
 * Chip — a small colored label pill for arbitrary tags (products, example
 * cases, detected patterns, severity, "human approval stays on" items).
 *
 * Unlike SkillPill (whose variant enum is tied to skill types), Chip's
 * `tone` is a generic semantic vocabulary shared with MeterBar's tones, so
 * the same color language reads consistently across the app.
 *
 * Props:
 *   label {string}
 *   tone  {'neutral'|'plain'|'good'|'warn'|'bad'|'info'|'orange'} — defaults
 *                       to 'neutral' (a plain outlined pill, used for tags
 *                       with no inherent severity/status, e.g. Products,
 *                       Example Cases). 'plain' is a fixed gray pill with
 *                       white text — used where a pill must never pick up
 *                       an accent color (e.g. the insight modal's tag row).
 *                       'orange' is distinct from 'warn' (yellow, used for
 *                       Medium severity) — it's the accent used for "human
 *                       approval stays on" guardrails.
 *   color {string} — optional CSS color that overrides `tone` entirely
 *                       (text + a tinted background/border derived from it
 *                       via color-mix, matching accentColors.js's formula).
 *                       Used by the Home insight modal to match a pill to
 *                       its card's accent instead of a fixed semantic tone.
 */
export default function Chip({ label, tone = 'neutral', color }) {
  const override = color
    ? {
        color,
        backgroundColor: `color-mix(in srgb, ${color} 14%, var(--color-blue-900))`,
        borderColor: `color-mix(in srgb, ${color} 40%, transparent)`,
      }
    : undefined

  return (
    <span className={`${styles.chip} ${styles[tone] ?? styles.neutral}`} style={override}>
      {label}
    </span>
  )
}
